import { useApolloClient, useSubscription } from '@apollo/client';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import DodatnoIcon from "src/assets/images/dodatno.png";
import { DisplayDateFormat, i18n } from 'src/common';
import {
  GetOfferDocument,
  OfferChangedDocument,
  useGetOfferQuery,
  type GetOfferQuery,
  type GetOfferQueryVariables,
} from 'src/common/api/apollo/additional';
import { isAdditionalGraphqlWsOpen } from 'src/common/api/apollo/additional/client';
import { AppRoutingAdditional } from 'src/pages/app';
import { baseWidths, colors } from 'src/theme';
import { Button } from '../buttons';
import { Flex } from '../grid';
import { Accordion } from '../other/accordion.other';
import { Text } from '../typography';

const isString = (x: unknown): x is string => typeof x === 'string';

export interface HeaderDataDetailRow {
  id?: string;
  label: string;
  value: string | React.ReactNode;
  disabled: boolean;
}

const HeaderDataStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  background: colors.background7,
  flexDirection: 'column',
  padding: '10px',
  position: 'sticky',
  top: 0,
});

const AccordionWrapper = styled(Flex)({
  width: '100%',
  '@media (min-width: 484px)': { width: baseWidths.mobile },
});

const PriceChangeNotice = styled(Text)({
  width: '100%',
  maxWidth: baseWidths.mobile,
  alignSelf: 'center',
  padding: '12px 16px',
  borderRadius: 12,
  backgroundColor: colors.success2,
  fontSize: 12,
  lineHeight: '22px',
  marginTop: 8,
});

const DetailsCard = styled.div({
  padding: '8px 8px',
  borderRadius: '4px',
  backgroundColor: colors.background6,
});

const PriceChangeNoticeLead = styled.strong({
});


const formatPrice = (n: number) => `${n.toFixed(2).replace('.', ',')}`;
const hasValue = (v: unknown) => v !== undefined && v !== null;

interface HeaderDataProps {
  expanded: boolean;
  handleStyleExpansion: (value: boolean) => void;
  /** Fires when the banner is actually shown (route + offer data) vs hidden. */
  onVisibilityChange?: (visible: boolean) => void;
}

export const HeaderData: React.FC<HeaderDataProps> = ({ expanded, handleStyleExpansion, onVisibilityChange }) => {
  const params = useParams<{ offerId: string }>();
  const [viewType, setViewType] = useState<string | null>(null);
  const location = useLocation();
  const client = useApolloClient();
  const offerId = params.offerId ?? '';
  const offerIdRef = useRef(offerId);
  offerIdRef.current = offerId;

  const subscriptionUpdatePendingRef = useRef(false);
  const prevYearlyCreditCardRef = useRef<number | null>(null);
  const [priceChangeNoticeVisible, setPriceChangeNoticeVisible] = useState(false);

  const isOnAdditionalRoute = useMemo(
    () => AppRoutingAdditional.some((route) => matchPath(route.path, location.pathname)),
    [location.pathname],
  );

  const { data: getOfferAdditionalData } = useGetOfferQuery({
    variables: { id: offerId },
    skip: !params.offerId || !isOnAdditionalRoute,
    fetchPolicy: 'cache-and-network',
  });

  const yearlyCreditCard =
    getOfferAdditionalData?.getOffer?.productTypeSpecific?.calculatedPrices?.total?.creditCard ?? null;

  useEffect(() => {
    prevYearlyCreditCardRef.current = null;
    subscriptionUpdatePendingRef.current = false;
    setPriceChangeNoticeVisible(false);
  }, [offerId]);

  useEffect(() => {
    if (!getOfferAdditionalData?.getOffer) {
      prevYearlyCreditCardRef.current = null;
      return;
    }
    const nextY = yearlyCreditCard ?? 0;

    if (subscriptionUpdatePendingRef.current) {
      subscriptionUpdatePendingRef.current = false;
      const prevY = prevYearlyCreditCardRef.current;
      if (prevY !== null && Math.abs(prevY - nextY) > 0.009) {
        setPriceChangeNoticeVisible(true);
      }
    }
    prevYearlyCreditCardRef.current = nextY;
  }, [yearlyCreditCard, getOfferAdditionalData?.getOffer]);

  useEffect(() => {
    if (!priceChangeNoticeVisible) return undefined;
    const t = window.setTimeout(() => setPriceChangeNoticeVisible(false), 8000);
    return () => window.clearTimeout(t);
  }, [priceChangeNoticeVisible]);

  const lastWsRestartRef = useRef(0);

  const { restart: restartOfferChangedSubscription } = useSubscription(OfferChangedDocument, {
    variables: { id: offerId },
    skip: !params.offerId || !isOnAdditionalRoute,
    onData: ({ data: subData }) => {
      const next = subData.data?.offerChanged as GetOfferQuery['getOffer'] | null | undefined;
      if (next === undefined) return;
      if (next !== null) {
        subscriptionUpdatePendingRef.current = true;
      }
      client.writeQuery<GetOfferQuery, GetOfferQueryVariables>({
        query: GetOfferDocument,
        variables: { id: offerId },
        data: { getOffer: next },
      });
    },
  });

  const tryRestartOfferSubscriptionIfSocketDead = useRef(() => {});

  tryRestartOfferSubscriptionIfSocketDead.current = () => {
    if (!offerIdRef.current || !navigator.onLine) return;
    const now = Date.now();
    if (now - lastWsRestartRef.current < 4000) return;
    if (isAdditionalGraphqlWsOpen()) return;
    lastWsRestartRef.current = now;
    restartOfferChangedSubscription();
  };

  /** When the user comes back (tab/window/network), reconnect the offer subscription if the WS dropped. */
  useEffect(() => {
    if (!params.offerId) return undefined;

    const onUserActiveCheckSocket = () => {
      if (isOnAdditionalRoute) {
        tryRestartOfferSubscriptionIfSocketDead.current()
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        onUserActiveCheckSocket();
      }
    };

    window.addEventListener('focus', onUserActiveCheckSocket);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onUserActiveCheckSocket);

    return () => {
      window.removeEventListener('focus', onUserActiveCheckSocket);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onUserActiveCheckSocket);
    };
  }, [params.offerId, restartOfferChangedSubscription]);

  const findBannerType = () => {
    for (const route of AppRoutingAdditional) {
      const match = matchPath(route.path, location.pathname);
      if (match) {
        const bannerViewType = route?.bannerData;
        if (bannerViewType) {
          handleStyleExpansion(false);
          setViewType(bannerViewType);
          return;
        }
      }
    }
    handleStyleExpansion(false);
    setViewType(null);
  };

  useEffect(() => {
    findBannerType();
  }, [location.pathname]);

  const isHeaderDataVisible = Boolean(viewType && getOfferAdditionalData);

  useEffect(() => {
    onVisibilityChange?.(isHeaderDataVisible);
  }, [isHeaderDataVisible, onVisibilityChange]);

  if (!viewType || !getOfferAdditionalData) return null;

  const perYear = i18n.t('layout.headerData.perYear');
  const perMonth = i18n.t('layout.headerData.perMonth');

  const yearlyPrice = getOfferAdditionalData?.getOffer?.productTypeSpecific?.calculatedPrices?.total?.creditCard ?? 0;
  const monthlyPrice = (getOfferAdditionalData?.getOffer?.productTypeSpecific?.calculatedPrices?.total?.creditCardWithInstallments ?? 0) / 12;

  const details: HeaderDataDetailRow[] = [
    {
      id: 'brandId',
      label: i18n.t('layout.headerData.insurance'),
      value: getOfferAdditionalData?.getOffer?.product?.brand?.shortName ?? '',
      disabled: !getOfferAdditionalData?.getOffer?.product?.brand?.shortName,
    },
    {
      id: 'packageName',
      label: i18n.t('layout.headerData.policy'),
      value: getOfferAdditionalData?.getOffer?.product?.name ?? '',
      disabled: !getOfferAdditionalData?.getOffer?.product?.name,
    },
    {
      id: 'coverages',
      label: i18n.t('layout.headerData.coverages'),
      value: getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.coverages
        ?.map((coverage) =>
          coverage.gratis
            ? `${coverage.label} (${i18n.t('layout.headerData.gratis')})`
            : `${coverage.label} (${formatPrice(coverage.price ?? 0)} €)`,
        )
        .join('\n') ?? '',
      disabled: !getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.coverages?.length,
    },
    {
      id: 'basePremium',
      label: i18n.t('layout.headerData.basePremium'),
      value: `${formatPrice(yearlyPrice! - (getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0))} €/god`,
      disabled: !(getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0) && (getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0) <= 0,
    },
    {
      id: 'discount',
      label: i18n.t('layout.headerData.discount'),
      value: getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier ? `${formatPrice(getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0)} €` : '',
      disabled: !(getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0) && (getOfferAdditionalData?.getOffer?.productTypeSpecific?.additional?.voucherPriceModifier?.amount ?? 0) <= 0,
    },
    {
      id: 'totalAmount',
      label: i18n.t('layout.headerData.totalAmount'),
      value: `<strong>${formatPrice(yearlyPrice!)} €/god</strong> (${formatPrice(monthlyPrice)} €/mj)`,
      disabled: !hasValue(yearlyPrice),
    },
    {
      id: 'policyStartDate',
      label: i18n.t('layout.headerData.policyStartDate'),
      value: getOfferAdditionalData?.getOffer?.productTypeSpecific?.insuredPerson?.newPolicyStartDate ? dayjs(getOfferAdditionalData?.getOffer?.productTypeSpecific?.insuredPerson?.newPolicyStartDate).format(DisplayDateFormat) : '',
      disabled: !getOfferAdditionalData?.getOffer?.productTypeSpecific?.insuredPerson?.newPolicyStartDate,
    },
  ];

  return (
    <HeaderDataStyle>
      <AccordionWrapper vertical gap={0} justify="stretch" align="stretch">
        <Accordion
          expandable={viewType !== 'price_details'}
          title={(
            <Flex justify="space-between">
              <Flex gap={16} align="center" justify="space-between">
                <img src={DodatnoIcon} alt="Dodatno" />
                <Flex vertical align="flex-start">
                  <Text type="medium" bold>{getOfferAdditionalData?.getOffer?.productTypeSpecific?.insuredPerson?.birthDate ? dayjs(getOfferAdditionalData?.getOffer?.productTypeSpecific?.insuredPerson?.birthDate).format(DisplayDateFormat) : ''}</Text>
                  <Text type="small">{`${formatPrice(yearlyPrice)} €/${perYear} (${formatPrice(monthlyPrice)} €/${perMonth})`}</Text>
                </Flex>
              </Flex>
              {viewType !== 'price_details' && (
                <Button
                  onClick={() => handleStyleExpansion(!expanded)}
                  text={expanded ? i18n.t('layout.headerData.showLess') : i18n.t('layout.headerData.details')}
                  size='small'
                  btnType='textPrimary'
                />
              )}
            </Flex>
          )}
          content={(
            <Flex vertical gap={2}>
              {details.filter((detail) => !detail.disabled).map((detail) => (
                <DetailsCard
                  key={detail.id ?? detail.label}
                >
                  <Flex gap={8} justify="space-between" align="center">
                    <Text type="small">{detail.label}</Text>
                    <Flex gap={8} align="flex-end" justify="flex-end" wrap="wrap" style={{ whiteSpace: 'pre-line', textAlign: 'right' }}>
                      {isString(detail.value) ? (
                        <Text type="small" dangerouslySetInnerHTML={{ __html: detail.value }}></Text>
                      ) : (
                        detail.value
                      )}
                    </Flex>
                  </Flex>
                </DetailsCard>
              ))}
            </Flex>
          )}
          gap={12}
          onChange={() => handleStyleExpansion(!expanded)}
          expandIconComponent={<></>}
        />
        {priceChangeNoticeVisible && (
          <PriceChangeNotice type="small" bold>
            <PriceChangeNoticeLead>{i18n.t('layout.headerData.priceChangeNoticeBold')}</PriceChangeNoticeLead>
            {i18n.t('layout.headerData.priceChangeNoticeRest')}
          </PriceChangeNotice>
        )}
      </AccordionWrapper>
    </HeaderDataStyle>
  );
};
